import { ClockIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSiteSettings } from "src/context/SiteSettingsContext";
import { WorkspaceContext } from "src/context/workspace";
import { useWorkspaceSwitching } from "src/context/WorkspaceSwitchingContext";
import { apiRequest } from "src/async/apiUtils";
import { getNavigateLink } from "src/helpers/userHelper";
import { toast } from "react-toastify";
import Preloader from "src/components/shared/LoadingAnimations/Preloader";

const WorkspacePaused = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { workspace_id } = useParams();
  const { site } = useSiteSettings();
  const { finishSwitching } = useWorkspaceSwitching();
  const { details: workspaceDetails } = useContext(WorkspaceContext);
  const [checkingStatus, setCheckingStatus] = useState(false);

  useEffect(() => {
    finishSwitching();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { message } = location.state || {};

  const handleCheckStatus = async () => {
    try {
      setCheckingStatus(true);
      const { data: resData } = await apiRequest("GET", workspace_id ? `/portal/users/${workspace_id}/page` : `/portal/users/workspaces/page`, {}, { returnErrorWithstatus: true });

      if (resData.status === 200) {
        toast.success("Workspace is active now!");
        const navigate_link = getNavigateLink(resData);
        navigate(navigate_link);
      } else if (["WORKSPACE_PAUSED", "WORKSPACE_PAUSED_SCHEDULED"].includes(resData?.code)) {
        toast.info(resData?.message || "Workspace is still paused.");
      } else {
        navigate("/permission-denied");
      }
    } catch (error) {
      if ((error?.status === 403 || error?.response?.data?.status === 403) && ["WORKSPACE_PAUSED", "WORKSPACE_PAUSED_SCHEDULED"].includes(error?.response?.data?.code)) {
        toast.info(error?.response?.data?.message || "Workspace is still paused.");
      } else if (error?.status === 402 || error?.response?.data?.status === 402) {
        navigate(`/workspace/${workspace_id}/payment-required`);
      } else {
        toast.error("Failed to verify workspace status. Please try again.");
      }
    } finally {
      setCheckingStatus(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  const logoSrc = site?.image_logo;
  const highlightColor = site?.highlight_color || "#0062A2";

  return (
    <div className="relative flex min-h-screen w-full flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 via-gray-100 to-indigo-50/30 p-6">
      {/* Dynamic Glow backgrounds */}
      <div 
        className="pointer-events-none absolute left-1/4 top-1/4 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
        style={{ backgroundColor: highlightColor, opacity: 0.12 }}
      ></div>
      <div 
        className="pointer-events-none absolute bottom-1/4 right-1/4 h-96 w-96 translate-x-1/2 translate-y-1/2 rounded-full blur-3xl"
        style={{ backgroundColor: highlightColor, opacity: 0.12 }}
      ></div>

      <div className="relative w-full max-w-lg rounded-3xl border border-white/60 bg-white/80 p-10 text-center shadow-[0_20px_50px_rgba(0,0,0,0.05)] backdrop-blur-xl transition-all duration-300 hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)] md:p-12">
        {logoSrc && (
          <div className="mb-10 flex justify-center drop-shadow-sm">
            <img
              src={logoSrc}
              alt="Workspace Logo"
              className="h-14 object-contain transition-transform duration-300 hover:scale-105"
            />
          </div>
        )}

        <div className="mb-8 flex justify-center">
          <div 
            className="relative flex h-24 w-24 items-center justify-center rounded-2xl border shadow-[inset_0_2px_10px_rgba(0,0,0,0.03)] transition-all duration-300"
            style={{ 
              backgroundColor: `${highlightColor}1A`, // 10% opacity
              borderColor: `${highlightColor}33`      // 20% opacity
            }}
          >
            <div 
              className="absolute inset-0 animate-ping rounded-2xl border opacity-20"
              style={{ borderColor: highlightColor }}
            ></div>
            <ClockIcon 
              className="h-12 w-12 animate-pulse" 
              style={{ color: highlightColor }}
            />
          </div>
        </div>

        <div className="space-y-4">
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-800 sm:text-4xl">Workspace Paused</h1>
          <p className="mx-auto max-w-md text-base font-medium leading-relaxed text-gray-600">{message || "This workspace has been temporarily paused."}</p>
        </div>

        <div className="mt-8 flex justify-center">
          {workspaceDetails?.pause_expires_at ? (
            <div 
              className="inline-flex items-center gap-2 rounded-2xl border px-5 py-3.5 text-sm shadow-sm backdrop-blur-sm"
              style={{ 
                backgroundColor: `${highlightColor}0D`, // 5% opacity
                borderColor: `${highlightColor}26`,     // 15% opacity
                color: highlightColor 
              }}
            >
              <ClockIcon className="h-5 w-5 flex-shrink-0" style={{ color: highlightColor }} />
              <span>
                Expected to resume on: <strong className="font-semibold">{formatDate(workspaceDetails?.pause_expires_at)}</strong>
              </span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200/40 bg-slate-50/80 px-5 py-3.5 text-sm text-gray-600 shadow-sm backdrop-blur-sm">
              <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-slate-500" />
              <span>Please contact your administrator for more information.</span>
            </div>
          )}
        </div>

        <div className="mt-10 flex justify-center">
          <button
            onClick={handleCheckStatus}
            type="button"
            disabled={checkingStatus}
            className={`focus:ring-highlightColor/50 group relative inline-flex w-full min-w-[200px] items-center justify-center rounded-2xl px-10 py-4 text-base font-bold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent sm:w-auto ${
              checkingStatus
                ? "cursor-not-allowed border border-gray-200 bg-gray-100 text-gray-400"
                : "shadow-highlightColor/10 hover:bg-highlightColor/95 hover:shadow-highlightColor/20 bg-highlightColor text-white shadow-md hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
            }`}>
            <div className="relative flex items-center">
              {checkingStatus && (
                <div className="mr-2">
                  <Preloader
                    circleDimension="4"
                    text=""
                  />
                </div>
              )}
              {checkingStatus ? "Verifying..." : "Check Again"}
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkspacePaused;
